import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import applicationsService from '../../services/applicationsService';
import { getAuthHeaders } from '../../services/authSession';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { RECRUITMENT_STATUSES, statusLabel } from '../../config/recruitmentStatuses';
import { REJECTION_REASONS, commonNextStages } from '../../config/recruitmentPipeline';
import { sourceLabel, normalizeSource } from '../../config/recruitmentSources';

const API_URL = process.env.REACT_APP_API_URL || '';

// Ngjyra e skorit AI sipas bandës së përshtatjes.
const aiScoreColor = (s) => (s >= 75 ? '#16A34A' : s >= 50 ? '#D97706' : '#DC2626');
const AI_VERDICT_LABEL = {
  i_rekomanduar_fort: 'I rekomanduar fort',
  i_rekomanduar: 'I rekomanduar',
  neutral: 'Neutral',
  i_dobet: 'Përshtatje e dobët',
};

// Ngjyrat dhe etiketat vijnë nga konfigurimi i përbashkët; këtu mbahet vetëm
// harta e çelësave, sepse dy vende e përdorin për të vërtetuar një status nga URL-ja.
const STATUS_COLOR = Object.fromEntries(RECRUITMENT_STATUSES.map((x) => [x.key, x.color]));

export default function ApplicationsList() {
  const [rows, setRows] = useState([]);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  // `?status=offer` — kartat e Përmbledhjes hapin listën e filtruar drejtpërdrejt.
  const location = useLocation();
  const initialStatus = new URLSearchParams(location.search).get('status');
  // Kartat e ngarkesës te Përmbledhja hapin radhën e një rekrutuesi të caktuar.
  // Vlera pranohet vetëm nëse është fjalë e njohur ose numër — një parametër i
  // shpikur do të kthente listë bosh pa shpjegim.
  const readRecruiter = (sp) => {
    const v = sp.get('recruiter');
    if (!v) return 'all';
    return ['all', 'me', 'unassigned'].includes(v) || /^\d+$/.test(v) ? v : 'all';
  };
  const initialRecruiter = readRecruiter(new URLSearchParams(location.search));
  const [filters, setFilters] = useState({
    q: '', status: STATUS_COLOR[initialStatus] ? initialStatus : 'all', from: '', to: '',
    // «Të miat» është pyetje e serverit, jo e klientit: kush jam unë vendoset
    // nga token-i, ndryshe çdokush do të mund të kërkonte radhën e tjetrit.
    recruiter: initialRecruiter,
  });
  // Kur URL-ja ndryshon ndërsa faqja është e hapur (p.sh. klik nga Përmbledhja
  // ose funnel-i), filtri ndjek URL-në — useState lexon vetëm hapjen e parë.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const st = sp.get('status');
    setFilters((f) => ({ ...f, status: STATUS_COLOR[st] ? st : 'all', recruiter: readRecruiter(sp) }));
  }, [location.search]);
  const [confirmId, setConfirmId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(0);            // MUI numëron nga 0
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Parametrat e serverit — kërkimi me vonesë të vogël.
   *
   * Pa të, çdo shkronjë e shtypur do të ishte një pyetje te baza. Njëqind
   * milisekonda e bëjnë shkrimin një kërkesë, jo dhjetë.
   */
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  // Çdo ndryshim filtri e kthen te faqja e parë: përndryshe një filtër i ri
  // mbi faqen e shtatë do të kthente listë bosh pa shpjegim.
  useEffect(() => { setPage(0); }, [debouncedQ, filters.status, filters.from, filters.to, filters.recruiter]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    applicationsService()
      .list({ page: page + 1, limit: rowsPerPage, search: debouncedQ, status: filters.status, from: filters.from, to: filters.to, recruiter: filters.recruiter })
      .then((r) => {
        if (!mounted) return;
        setRows(r.rows);
        setPagination(r.pagination);
        setStats(r.stats);
      })
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, [page, rowsPerPage, debouncedQ, filters.status, filters.from, filters.to, filters.recruiter, reloadKey]);

  // ── Rekrutuesët ───────────────────────────────────────────────────────────
  const [recruiters, setRecruiters] = useState([]);
  useEffect(() => { applicationsService().recruiters().then(setRecruiters); }, []);

  /* ── Zgjedhja e shumëfishtë ────────────────────────────────────────────────
   *
   * Zgjedhja mbahet si id-ra, jo si rreshta: rreshtat zëvendësohen në çdo
   * ringarkim, kurse id-ja mbetet. Ajo pastrohet sa herë ndryshon grupi i
   * shfaqur — një veprim mbi kandidatë që përdoruesi nuk i shikon më është
   * pikërisht gabimi që veprimet masive rrezikojnë të bëjnë të heshtur.
   */
  const [selected, setSelected] = useState([]);
  useEffect(() => { setSelected([]); }, [page, rowsPerPage, debouncedQ, filters.status, filters.from, filters.to, filters.recruiter]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAllOnPage = () => setSelected((s) => (rows.every((r) => s.includes(r.id)) ? [] : rows.map((r) => r.id)));

  // Fazat e ofruara: vetëm ato që janë hap i lejuar për ÇDO kandidat të zgjedhur.
  // Një faza e pranueshme për gjysmën do të prodhonte gjysmën e dështimeve.
  const bulkStages = useMemo(
    () => commonNextStages(rows.filter((r) => selectedSet.has(r.id)).map((r) => r.status)),
    [rows, selectedSet]);

  const [bulkBusy, setBulkBusy] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectForm, setRejectForm] = useState({ rejectionCode: '', rejectReason: '' });

  const runBulk = async (action, data, busyLabel) => {
    setBulkBusy(busyLabel); setBulkError(''); setBulkResult(null);
    const res = await applicationsService().bulkAction({ action, ids: selected, data });
    setBulkBusy('');
    if (res?.error) { setBulkError(res.error); return false; }
    setBulkResult({ action, ...res });
    setSelected([]);
    setReloadKey((k) => k + 1);
    return true;
  };

  const remove = async (id) => {
    await applicationsService().remove(id);
    setRows((r) => r.filter((x) => x.id !== id));
    setConfirmId(null);
  };

  // ── Vlerësimi me AI ────────────────────────────────────────────────────────
  const [aiLoading, setAiLoading] = useState({}); // { [id]: true }
  const [aiError, setAiError] = useState('');

  const runAiScreen = async (row) => {
    setAiLoading((p) => ({ ...p, [row.id]: true }));
    setAiError('');
    try {
      const res = await fetch(`${API_URL}/api/applications/${row.id}/ai-screen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Vlerësimi dështoi');
      const patch = { aiScore: d.data.aiScore, aiEvaluation: d.data.aiEvaluation, aiEvaluatedAt: d.data.aiEvaluatedAt };
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, ...patch } : x)));
      setCurrent((c) => (c && c.id === row.id ? { ...c, ...patch } : c));
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading((p) => ({ ...p, [row.id]: false }));
    }
  };

  // Rreshtat vijnë tashmë të filtruar dhe të renditur nga serveri.
  const filteredRows = rows;

  // Numëruesit llogariten mbi TË GJITHË rreshtat e filtruar, jo mbi faqen:
  // «7 kandidatë» nuk duhet të thotë «7 në këtë faqe».
  const counts = stats?.byStatus || {};
  const totals = useMemo(() => {
    const total = stats?.total ?? 0;
    const hired = stats?.hired ?? 0;
    return {
      total, hired,
      rejected: stats?.rejected ?? 0,
      inProcess: stats?.inProcess ?? 0,
      conversion: total ? Math.round((hired / total) * 100) : 0,
    };
  }, [stats]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const confirmTarget = useMemo(() => rows.find((r) => r.id === confirmId) || null, [confirmId, rows]);

  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" component={Link} to="/applications/funnel">Funnel</Button>
        <Button variant="outlined" color="secondary" component={Link} to="/job-postings">Menaxho pozitat</Button>
        <Button variant="contained" component={Link} to="/applications/add">Shto kandidat</Button>
      </Stack>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Kërko"
                placeholder="Emër, email, pozitë..."
                fullWidth
                value={filters.q}
                onChange={setFilter('q')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField select label="Statusi" fullWidth value={filters.status} onChange={setFilter('status')}>
                <MenuItem value="all">Të gjithë</MenuItem>
                {Object.keys(STATUS_COLOR).map((s) => (
                  <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Prej"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={filters.from}
                onChange={setFilter('from')}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Deri"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={filters.to}
                onChange={setFilter('to')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField select label="Rekrutuesi" fullWidth value={filters.recruiter} onChange={setFilter('recruiter')}>
                <MenuItem value="all">Të gjithë</MenuItem>
                <MenuItem value="me">Radha ime</MenuItem>
                <MenuItem value="unassigned">Pa rekrutues</MenuItem>
                {recruiters.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button fullWidth variant="outlined" onClick={() => setFilters({ q: '', status: 'all', from: '', to: '', recruiter: 'all' })}>Reseto filtrat</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
        <Card sx={{ flex: 1, borderLeft: '4px solid #3E8FA3' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Totali i kandidatëve</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{totals.total}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Në proces</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{totals.inProcess}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Punësuar</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{totals.hired}</Typography>
            <Typography variant="caption" color="text.secondary">Konvertim {totals.conversion}%</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Refuzuar</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{totals.rejected}</Typography>
          </CardContent>
        </Card>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {Object.entries(counts).map(([k, v]) => (
          <Chip key={k} color={STATUS_COLOR[k] || 'default'} label={`${statusLabel(k)} • ${v}`} />
        ))}
      </Stack>
      <Card>
        <CardContent>
          {/* ── Veprime masive ────────────────────────────────────────────────
              Shiriti shfaqet vetëm me zgjedhje aktive, dhe tregon numrin: një
              veprim mbi «të gjithë» pa e ditur se sa janë është veprim verbtazi. */}
          {selected.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: '#E6F4F8', border: '1px solid #BFE0E8' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2C6E7E', whiteSpace: 'nowrap' }}>
                  {selected.length} të zgjedhur
                </Typography>
                <TextField
                  select size="small" label="Caktoje rekrutuesin" sx={{ minWidth: 200 }} value=""
                  disabled={!!bulkBusy}
                  onChange={(e) => {
                    const v = e.target.value;
                    runBulk('assign_recruiter', { recruiterUserId: v === 'none' ? null : v }, 'assign');
                  }}
                >
                  {recruiters.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
                  <Divider />
                  <MenuItem value="none">Hiqe rekrutuesin</MenuItem>
                </TextField>
                <TextField
                  select size="small" label="Kaloji te faza" sx={{ minWidth: 190 }} value=""
                  disabled={!!bulkBusy || bulkStages.length === 0}
                  helperText={bulkStages.length === 0 ? 'Zgjedhja ka faza që nuk ndajnë një hap të përbashkët.' : ' '}
                  onChange={(e) => runBulk('change_stage', { status: e.target.value }, 'stage')}
                >
                  {bulkStages.map((st) => <MenuItem key={st} value={st}>{statusLabel(st)}</MenuItem>)}
                </TextField>
                <Button size="small" color="error" variant="outlined" disabled={!!bulkBusy}
                  onClick={() => { setRejectForm({ rejectionCode: '', rejectReason: '' }); setRejectOpen(true); }}>
                  Refuzo të zgjedhurit
                </Button>
                <Box sx={{ flex: 1 }} />
                {bulkBusy && <CircularProgress size={18} />}
                <Button size="small" onClick={() => setSelected([])}>Hiq zgjedhjen</Button>
              </Stack>
            </Box>
          )}
          {bulkError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkError('')}>{bulkError}</Alert>}
          {/* Suksesi i pjesshëm raportohet si i pjesshëm — «u krye» mbi 43 nga 50
              do të lejonte shtatë kandidatë të mbeteshin pa e ditur askush. */}
          {bulkResult && (
            <Alert
              severity={bulkResult.failed ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setBulkResult(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {bulkResult.succeeded} nga {bulkResult.selected} u përpunuan
                {bulkResult.failed ? ` • ${bulkResult.failed} mbetën` : ''}
              </Typography>
              {(bulkResult.failures || []).map((f, i) => (
                <Typography key={i} variant="caption" display="block">
                  {f.count}× {f.reason} (#{f.ids.slice(0, 12).join(', #')}{f.ids.length > 12 ? '…' : ''})
                </Typography>
              ))}
            </Alert>
          )}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
          ) : filteredRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {/* Me filtra aktivë, «s'ka ende kandidatë» është e pavërtetë — ka,
                  por jo këtu. Dy fjali të ndara, sepse veprimi është tjetër. */}
              {(debouncedQ || filters.status !== 'all' || filters.from || filters.to)
                ? 'Asnjë kandidat nuk i përgjigjet filtrave të zgjedhur.'
                : (<>S’ka ende kandidatë. Aplikimet nga <Link to="/job-postings" style={{ color: '#2C6E7E', fontWeight: 600 }}>pozitat e publikuara</Link> vijnë
                  këtu automatikisht sapo kandidati dërgon formularin — "Shto kandidat" duhet vetëm për kandidatë të sjellë manualisht (email, rekomandim etj.).</>)}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={rows.length > 0 && rows.every((r) => selectedSet.has(r.id))}
                      indeterminate={selected.length > 0 && !rows.every((r) => selectedSet.has(r.id))}
                      onChange={toggleAllOnPage}
                      inputProps={{ 'aria-label': 'Zgjidh të gjithë në këtë faqe' }}
                    />
                  </TableCell>
                  <TableCell>Kandidati</TableCell>
                  <TableCell>Lista</TableCell>
                  <TableCell>Pozita</TableCell>
                  <TableCell>Burimi</TableCell>
                  <TableCell>Statusi</TableCell>
                  <TableCell>Rekrutuesi</TableCell>
                  <TableCell>AI</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Punonjësi</TableCell>
                  <TableCell align="right">Veprime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r) => (
                  <TableRow key={r.id} hover selected={selectedSet.has(r.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedSet.has(r.id)} onChange={() => toggleOne(r.id)} />
                    </TableCell>
                    <TableCell>{r.fullName}</TableCell>
                    <TableCell>{r.listName || '-'}</TableCell>
                    <TableCell>{r.position}</TableCell>
                    <TableCell>
                      {/* «LinkedIn», «linkedin» dhe «LI» janë i njëjti kanal:
                          etiketa vjen nga fjalori, jo nga ajo që u shkrua. */}
                      {normalizeSource(r.source) === 'public'
                        ? <Chip size="small" label="Faqja publike" sx={{ bgcolor: '#E6F4F8', color: '#2C6E7E', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                        : sourceLabel(r.source)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[r.status] || 'default'} label={statusLabel(r.status)} />
                    </TableCell>
                    <TableCell>
                      {r.recruiterName
                        ? <Chip size="small" variant="outlined" icon={<PersonOutlineIcon />} label={r.recruiterName} />
                        : <Typography variant="caption" color="text.disabled">Pa rekrutues</Typography>}
                    </TableCell>
                    <TableCell>
                      {aiLoading[r.id] ? (
                        <CircularProgress size={18} />
                      ) : r.aiScore != null ? (
                        <Tooltip title={AI_VERDICT_LABEL[r.aiEvaluation?.verdict] || 'Vlerësim AI — kliko ⓘ për detaje'}>
                          <Chip
                            size="small"
                            icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />}
                            label={r.aiScore}
                            sx={{
                              fontWeight: 700,
                              color: aiScoreColor(r.aiScore),
                              bgcolor: `${aiScoreColor(r.aiScore)}1A`,
                              '& .MuiChip-icon': { color: aiScoreColor(r.aiScore) },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Vlerëso me AI">
                          <IconButton size="small" color="primary" onClick={() => runAiScreen(r)}>
                            <AutoAwesomeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{r.appliedAt ? new Date(r.appliedAt).toLocaleDateString() : ''}</TableCell>
                    <TableCell>
                      {r.workerId ? (
                        <Link to={`/workerlist/editworker/${r.workerId}`}>Punëtor</Link>
                      ) : ('-')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { setCurrent(r); setOpen(true); }}><InfoOutlinedIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => history.push(`/applications/edit/${r.id}`)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setConfirmId(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Paginimi te serveri: faqja mban njëzet e pesë rreshta, jo pesë mijë. */}
          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.min(page, Math.max(0, pagination.totalPages - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rreshta:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Refuzim masiv ─────────────────────────────────────────────────────
          Arsyeja është e detyrueshme dhe e strukturuar: pa kod, raportet e
          refuzimeve do të ishin një listë e zbrazët, dhe kandidati nuk do të
          mësonte kurrë përse. Serveri e kërkon të njëjtën gjë — kjo formë vetëm
          e bën të dukshme përpara dërgimit. */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Refuzo {selected.length} kandidatë</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Veprimi shkruan arsyen te çdo kandidat i zgjedhur. Të punësuarit dhe të
              refuzuarit më parë do të kalohen dhe do të raportohen veçmas.
            </Alert>
            <TextField
              select label="Arsyeja" fullWidth required
              value={rejectForm.rejectionCode}
              onChange={(e) => setRejectForm((f) => ({ ...f, rejectionCode: e.target.value }))}
            >
              {REJECTION_REASONS.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
            </TextField>
            <TextField
              label={rejectForm.rejectionCode === 'other' ? 'Shpjego arsyen (e detyrueshme)' : 'Shënim (opsional)'}
              fullWidth multiline minRows={2}
              required={rejectForm.rejectionCode === 'other'}
              value={rejectForm.rejectReason}
              onChange={(e) => setRejectForm((f) => ({ ...f, rejectReason: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Anulo</Button>
          <Button
            color="error" variant="contained"
            disabled={!rejectForm.rejectionCode
              || (rejectForm.rejectionCode === 'other' && !rejectForm.rejectReason.trim())
              || !!bulkBusy}
            onClick={async () => {
              const done = await runBulk('reject', rejectForm, 'reject');
              if (done) setRejectOpen(false);
            }}
          >
            Refuzo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detajet e kandidatit</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar>{(current?.fullName || '?').slice(0, 1)}</Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{current?.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">{current?.position} • {current?.listName || '—'}</Typography>
              </Box>
              <Chip sx={{ ml: 'auto' }} label={statusLabel(current?.status)} color={STATUS_COLOR[current?.status] || 'default'} />
            </Stack>
            <Stack direction="row" spacing={1}>
              {current?.email && (
                <Chip icon={<MailOutlineIcon />} label={current.email} />
              )}
              {current?.phone && (
                <Chip icon={<PhoneIcon />} label={current.phone} />
              )}
              {current?.source && (
                <Chip label={`Burimi: ${sourceLabel(current.source)}`} />
              )}
            </Stack>
            {/* Dokumentet e ngarkuara nga kandidati */}
            {Array.isArray(current?.files) && current.files.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Dokumentet</Typography>
                <Stack spacing={0.5}>
                  {current.files.map((f, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                      <InsertDriveFileIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      {f.stored ? (
                        <a
                          href={`${API_URL}/api/applications/${current.id}/documents/${f.stored}`}
                          onClick={async (e) => {
                            // Shkarkim me auth header — linku i thjeshtë s'e bart token-in.
                            e.preventDefault();
                            const resp = await fetch(`${API_URL}/api/applications/${current.id}/documents/${f.stored}`, { headers: getAuthHeaders() });
                            if (!resp.ok) return;
                            const blob = await resp.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = f.name || 'dokument'; a.click();
                            URL.revokeObjectURL(url);
                          }}
                          style={{ fontSize: '0.85rem' }}
                        >
                          {f.name}
                        </a>
                      ) : (
                        <Typography variant="body2" color="text.disabled">{f.name} (pa skedar)</Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {/* Vlerësimi me AI */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Vlerësimi me AI</Typography>
              {current?.aiScore != null && (
                <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />}
                  label={`${current.aiScore}/100 · ${AI_VERDICT_LABEL[current.aiEvaluation?.verdict] || ''}`}
                  sx={{ fontWeight: 700, color: aiScoreColor(current.aiScore), bgcolor: `${aiScoreColor(current.aiScore)}1A`,
                    '& .MuiChip-icon': { color: aiScoreColor(current.aiScore) } }} />
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" startIcon={aiLoading[current?.id] ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                disabled={!!aiLoading[current?.id]}
                onClick={() => current && runAiScreen(current)}>
                {current?.aiScore != null ? 'Rivlerëso' : 'Vlerëso me AI'}
              </Button>
            </Stack>
            {aiError && <Alert severity="warning" onClose={() => setAiError('')}>{aiError}</Alert>}
            {current?.aiEvaluation && (
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{current.aiEvaluation.summary}</Typography>
                {current.aiEvaluation.strengths?.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#16A34A' }}>PIKAT E FORTA</Typography>
                    {current.aiEvaluation.strengths.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary">• {s}</Typography>
                    ))}
                  </>
                )}
                {current.aiEvaluation.concerns?.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#D97706', display: 'block', mt: 0.75 }}>SHQETËSIMET</Typography>
                    {current.aiEvaluation.concerns.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary">• {s}</Typography>
                    ))}
                  </>
                )}
                {current.aiEvaluation.interview_questions?.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2C6E7E', display: 'block', mt: 0.75 }}>PYETJE PËR INTERVISTË</Typography>
                    {current.aiEvaluation.interview_questions.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary">{i + 1}. {s}</Typography>
                    ))}
                  </>
                )}
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Historiku</Typography>
            {current?.history?.length ? (
              <Stack spacing={1}>
                {current.history.map((h, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    {new Date(h.date).toLocaleString()} — {h.type === 'status' ? `${h.from || ''} → ${h.to}` : 'Koment'} {h.comment ? `– ${h.comment}` : ''}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">S’ka historik.</Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmId)} onClose={() => setConfirmId(null)}>
        <DialogTitle>Fshij aplikimin?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Ky veprim nuk mund të kthehet mbrapa. Jeni i sigurt që dëshironi të fshini aplikimin
            {confirmTarget ? ` e ${confirmTarget.fullName} (${confirmTarget.position})` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={() => confirmId && remove(confirmId)}>Fshij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
